import { CSSObject } from '@emotion/react';
import { type SVGProps, forwardRef } from 'react';

export const iconSizes = {
  xsmall: 16,
  small: 20,
  medium: 24,
} as const;

export type IconSize = keyof typeof iconSizes;

export type IconTypes = 'filled' | 'stroke';

export type IconProps = Omit<SVGProps<SVGSVGElement>, 'ref'> & {
  size?: number | IconSize;
  fill?: string;
  'data-testid'?: string;
  type?: IconTypes;
  width?: number | string;
  height?: number | string;
  css?: CSSObject;
};

export const IconBase = forwardRef<SVGSVGElement, IconProps>(function IconBase(
  {
    children,
    fill = 'none',
    viewBox,
    'data-testid': testId,
    size = 18,
    width,
    height,
    ...rest
  },
  ref,
) {
  const baseSize = typeof size === 'number' ? size : iconSizes[size];

  const hasAccessibleText = Boolean(
    rest['aria-label'] ||
      rest['aria-labelledby'] ||
      (rest.role && rest.role !== 'presentation' && rest.role !== 'none'),
  );

  const role = rest.role || (!hasAccessibleText ? 'presentation' : undefined);

  return (
    <svg
      ref={ref}
      xmlns='http://www.w3.org/2000/svg'
      width={width ?? baseSize}
      height={height ?? baseSize}
      viewBox={viewBox}
      fill={fill}
      data-testid={testId}
      css={{
        flexShrink: 0,
      }}
      role={role}
      {...rest}
    >
      {children}
    </svg>
  );
});
